import { useEffect, useState } from "react";
import {
  commitFile,
  listHistory,
  readAtCommit,
  isGitAvailable,
  type GitCommitInfo,
} from "../../state/version-actions.js";
import { useT } from "../../i18n/use-t.js";

/**
 * 버전 기록 패널 — 결정 #30(브라우저 내 Git, 로컬 전용). 경로 기반으로 동작해 게임
 * 카드 코드 모드·스크립트 편집기 양쪽에서 재사용한다. 현재 내용을 커밋하고, 히스토리를
 * 보고, 특정 버전을 미리보기/복원한다. 원격·토큰 없음(로컬 전용, 새 비밀 표면 0).
 *
 * git 모듈은 lazy-load라, 이 패널을 펼칠 때 처음 로드된다(초기 번들 0 영향, 결정 #1).
 */
export function VersionHistoryPanel({
  path,
  value,
  onRestore,
}: {
  /** 버전 추적 경로(cards/<id>.json, scripts/<id>.js 등). */
  path: string;
  /** 커밋할 현재 내용. */
  value: string;
  /** 복원 시 호출(에디터 내용 교체). */
  onRestore: (content: string) => void;
}) {
  const { t } = useT();
  const [available, setAvailable] = useState<boolean | null>(null);
  const [history, setHistory] = useState<GitCommitInfo[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [previewOid, setPreviewOid] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string>("");

  const reload = async () => {
    setHistory(await listHistory(path));
  };

  useEffect(() => {
    let alive = true;
    void isGitAvailable().then((ok) => {
      if (!alive) return;
      setAvailable(ok);
      if (ok) void reload();
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const commit = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const oid = await commitFile(path, value, message.trim() || t("version.defaultMessage"));
      setMessage("");
      if (oid) {
        setNotice(t("version.saved"));
        await reload();
      } else {
        setNotice(t("version.noChangesToSave"));
      }
    } catch (e) {
      setNotice(t("version.saveFailed", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const preview = async (oid: string) => {
    if (previewOid === oid) {
      setPreviewOid(null);
      return;
    }
    const text = await readAtCommit(oid, path);
    setPreviewText(text ?? t("version.unreadable"));
    setPreviewOid(oid);
  };

  const restore = (text: string) => {
    onRestore(text);
    setPreviewOid(null);
    setNotice(t("version.restored"));
  };

  if (available === false) {
    return (
      <div className="rounded-lg border border-line bg-bg-soft p-2 text-xs text-ink-faint">
        {t("version.unavailable")}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-line bg-bg-soft p-2">
      <div className="text-xs font-semibold text-ink-faint">{t("version.title")}</div>

      <div className="flex gap-2">
        <input
          className="min-w-0 flex-1 rounded border border-line bg-bg-raised px-2 py-1 text-sm text-ink outline-none focus:border-accent"
          placeholder={t("version.messagePlaceholder")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void commit();
          }}
          disabled={busy}
        />
        <button
          className="shrink-0 rounded-lg bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={() => void commit()}
          disabled={busy || !value.trim()}
        >
          {t("version.save")}
        </button>
      </div>

      {notice && <p className="text-xs text-ink-soft">{notice}</p>}

      {history.length === 0 ? (
        <p className="text-xs text-ink-faint">{t("version.empty")}</p>
      ) : (
        <ul className="space-y-1">
          {history.map((c) => (
            <li key={c.oid} className="rounded-lg border border-line bg-bg-raised px-2 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm text-ink">{c.message}</div>
                  <div className="text-[11px] text-ink-faint">
                    {new Date(c.timestamp).toLocaleString()} · {c.oid.slice(0, 7)}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    className="rounded border border-line px-2 py-0.5 text-xs text-ink-soft hover:bg-bg-soft"
                    onClick={() => void preview(c.oid)}
                  >
                    {previewOid === c.oid ? t("misc.close") : t("version.view")}
                  </button>
                </div>
              </div>

              {previewOid === c.oid && (
                <div className="mt-2 space-y-1">
                  <pre className="max-h-48 overflow-auto rounded bg-bg-soft p-2 font-mono text-xs text-ink">
                    {previewText}
                  </pre>
                  <button
                    className="rounded-lg border border-accent px-2 py-0.5 text-xs text-accent-soft hover:bg-bg-soft"
                    onClick={() => restore(previewText)}
                  >
                    {t("version.restore")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
